/**
 * Shown inside the AppShell when the visitor is authenticated but their portal
 * account is not yet linked to a member (customer) — i.e. `/me` returns
 * `scopes: []`. Every member-data endpoint (orders / vehicles / shipments /
 * invoices / documents / recommendations / favourites) 401s without a customer
 * scope, so instead of letting those pages mount (and bounce the signed-in user
 * back to /login on the 401) we render this calm, informative panel.
 *
 * A linked account arrives via the AS-primary provisioning flow (register on
 * AUCNET Stock → Customer Core → CP auto-provision with a customer scope) or an
 * operator invitation. Until then this is the expected state for a bare
 * self-registered / OAuth account.
 */
interface Props {
  email: string | null;
  onSignOut: () => void | Promise<void>;
}

export function MembershipPending({ email, onSignOut }: Props): JSX.Element {
  return (
    <section
      role="status"
      aria-live="polite"
      className="mx-auto max-w-xl rounded-xl border border-black/10 bg-white p-8 shadow-sm"
    >
      <h1 className="text-xl font-bold text-brand-900">Your account isn’t linked yet</h1>
      <p className="mt-3 text-sm leading-relaxed text-foreground/80">
        You’re signed in{email ? ` as ${email}` : ''}, but this account isn’t linked to a member
        profile yet. Your orders, vehicles, shipments and invoices become available once the link is
        complete.
      </p>
      <p className="mt-3 text-sm leading-relaxed text-foreground/80">
        If you just registered, your membership is still being set up. If this persists, please
        contact your Provide&nbsp;Cars representative.
      </p>
      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={() => void onSignOut()}
          className="rounded-md bg-brand-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-800"
        >
          Sign out
        </button>
      </div>
    </section>
  );
}
